import { ApiClient } from '../core/api-client';
import { logger } from '../../utils/logger';

export type CalendarEventType = 'fast' | 'ekadashi' | 'festival' | 'appearance' | 'disappearance' | 'special';

export interface CalendarEvent {
  id: number;
  title: string;
  description?: string | null;
  eventDate: string; // YYYY-MM-DD
  eventType: CalendarEventType;
  /** All types for this event. Empty array for old records; fall back to [eventType] in that case. */
  eventTags: CalendarEventType[];
  createdAt: string;
  updatedAt: string;
}

export interface CalendarEventRequest {
  title: string;
  description?: string | null;
  eventDate: string; // YYYY-MM-DD
  /** Primary type (first of eventTags). Required for backward compatibility. */
  eventType: CalendarEventType;
  /** Full list of types including the primary one. */
  eventTags?: CalendarEventType[];
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export class CalendarService {
  private client: ApiClient;

  constructor() {
    this.client = ApiClient.getInstance(API_BASE_URL);
  }

  async listEvents(params?: {
    start?: string;
    end?: string;
    types?: CalendarEventType[];
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<CalendarEvent[]> {
    const query = new URLSearchParams();
    if (params?.start) query.append('start', params.start);
    if (params?.end) query.append('end', params.end);
    if (params?.types && params.types.length > 0) query.append('types', params.types.join(','));
    if (params?.search) query.append('search', params.search);
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.offset) query.append('offset', String(params.offset));

    const url = `/congregation/calendar/events${query.toString() ? `?${query}` : ''}`;
    const response = await this.client.get<{ events: CalendarEvent[] }>(url, { credentials: 'include' });
    return response.data?.events || [];
  }

  async createEvent(payload: CalendarEventRequest): Promise<{ id: number }> {
    const response = await this.client.post<{ id: number }>(
      '/congregation/admin/calendar/events',
      payload,
      { credentials: 'include' },
    );
    return response.data;
  }

  async updateEvent(eventId: number, payload: CalendarEventRequest): Promise<{ status: string }> {
    const response = await this.client.put<{ status: string }>(
      `/congregation/admin/calendar/events/${eventId}`,
      payload,
      { credentials: 'include' },
    );
    return response.data;
  }

  async deleteEvent(eventId: number): Promise<{ status: string }> {
    const response = await this.client.delete<{ status: string }>(
      `/congregation/admin/calendar/events/${eventId}`,
      { credentials: 'include' },
    );
    return response.data;
  }
}

export const calendarService = new CalendarService();
